<template lang="pug">
svg.FcwMapMarker(
  version="1.1"
  xmlns="http://www.w3.org/2000/svg"
  xmlns:xlink="http://www.w3.org/1999/xlink"
  viewBox="0 0 64 64"
)
  template(v-if="shape === 'rounded'")
    BaseRounded(
      v-if="!selected"
      v-bind="markerBaseProps"
    )
    BaseRoundedSelected(
      v-else
      v-bind="markerBaseProps"
    )
    ProgressRounded(
      v-if="!selected && progress > 0"
      :line-color="lineColor"
      :progress="progress"
      :x="11"
      :y="16"
      :width="42"
      :height="40"
    )
  template(v-else-if="shape === 'squared'")
    BaseSquared(
      v-if="!selected"
      v-bind="markerBaseProps"
    )
    BaseSquaredSelected(
      v-else
      v-bind="markerBaseProps"
    )
    ProgressSquared(
      v-if="!selected && progress > 0"
      :line-color="lineColor"
      :progress
      :x="11"
      :y="16"
      :width="42"
      :height="40"
    )
  template(v-if="progress > 51")
    PinTail(:line-color)

  component(
    :is="iconMap[icon]"
    v-if="type === 'icon' && icon"
    :color
    :line-color
  )
  text(
    v-else-if="type === 'numeric'"
    :style="textStyle"
    :fill="lineColor"
    x="32"
    y="38"
    dominant-baseline="middle"
    text-anchor="middle"
  ) {{ value }}
  template(v-if="attributes.length === 1")
    component(
      :is="attributeMap[attributes[0]]"
      v-if="attributes[0]"
      :color
      :line-color
      :x="40"
      :y="14"
    )
  template(v-if="attributes.length > 1")
    component(
      :is="attributeMap[attributes[0]]"
      v-if="attributes[0]"
      :color
      :line-color
      :x="36"
      :y="10"
    )
    component(
      :is="attributeMap[attributes[1]]"
      v-if="attributes[1]"
      :color
      :line-color
      :x="44"
      :y="20"
    )
</template>
<script setup lang="ts">
import BaseRounded from './base/BaseRounded.vue';
import BaseRoundedSelected from './base/BaseRoundedSelected.vue';
import BaseSquared from './base/BaseSquared.vue';
import BaseSquaredSelected from './base/BaseSquaredSelected.vue';
import PinTail from './base/PinTail.vue';
import ProgressRounded from './progress/ProgressRounded.vue';
import ProgressSquared from './progress/ProgressSquared.vue';
import PaymentTerminal from './attributes/PaymentTerminal.vue';
import ElectricVehicle from './attributes/ElectricVehicle.vue';
import Forbidden from './icons/Forbidden.vue';
import PinPlace from './icons/PinPlace.vue';
import LandMark from './icons/LandMark.vue';
import Lock from './icons/Lock.vue';
import Maintenance from './icons/Maintenance.vue';
import Parking from './icons/Parking.vue';

export interface FcwMapMarkerProps {
  /**
   * Shape of the marker
   */
  shape?: 'rounded' | 'squared';
  /**
   * Type of the marker
   */
  type?: 'icon' | 'numeric';
  /**
   * If type is numeric, the value to display
   */
  value?: number;
  /**
   * Icon to display
   */
  icon?: keyof typeof iconMap;
  /**
   * Color of the icon, color name or hex value
   */
  color?: string;
  /**
   * Whether the icon is selected
   */
  selected?: boolean;
  /**
   * Whether the icon colors are inverted
   */
  inverted?: boolean;
  /**
   * Progress to display
   */
  progress?: number;
  /**
   * Attributes of the marker
   */
  attributes?: (keyof typeof attributeMap)[];
}

const props = withDefaults(defineProps<FcwMapMarkerProps>(), {
  selected: false,
  icon: undefined,
  shape: 'squared',
  type: 'numeric',
  color: '#FF0062',
  value: 0,
  inverted: false,
  progress: 0,
  attributes: () => [],
});

const iconMap = {
  forbidden: Forbidden,
  'bike-house': LandMark,
  'bike-locker': Lock,
  maintenance: Maintenance,
  parking: Parking,
  info: PinPlace,
};

const attributeMap = {
  'electric-vehicle': ElectricVehicle,
  'payment-terminal': PaymentTerminal,
};

const { clientState } = useClientStore();

const color = computed(() =>
  props.inverted ? clientState.theme.colorNeutralLight5 : props.color
);
const lineColor = computed(() =>
  props.inverted ? props.color : clientState.theme.colorNeutralLight5
);

const markerBaseProps = computed(() => ({
  x: 9,
  y: 14,
  width: 46,
  height: 50,
  color: color.value,
  lineColor: lineColor.value,
}));

const textStyle = computed<Style>(() => ({
  fontFamily: 'Roboto, sans-serif',
  fontSize: '20px',
  fontWeight: '900',
}));
</script>
